import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAdminAlbums,
  createAlbum,
  updateAlbum,
  deleteAlbum,
  uploadImage,
} from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Download, Image, Link as LinkIcon, Plus, Edit, Trash2, Upload, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { optimizeGalleryImage } from '@/lib/imageCompression';

export default function AdminGaleria() {
  const [showForm, setShowForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_url: '',
    photos: [],
    download_url: '',
    event_date: '',
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: albums = [], isLoading } = useQuery({
    queryKey: ['adminAlbums'],
    queryFn: () => listAdminAlbums(100)
  });

  const createMutation = useMutation({
    mutationFn: (data) => createAlbum(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast.success('Álbum criado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateAlbum(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast.success('Álbum atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteAlbum(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['albums'] });
      toast.success('Álbum excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      cover_url: '',
      photos: [],
      download_url: '',
      event_date: '',
      is_active: true
    });
    setEditingAlbum(null);
    setShowForm(false);
  };

  const handleEdit = (album) => {
    setEditingAlbum(album);
    setFormData({
      name: album.name || '',
      description: album.description || '',
      cover_url: album.cover_url || '',
      photos: album.photos || [],
      download_url: album.download_url || '',
      event_date: album.event_date || '',
      is_active: album.is_active ?? true
    });
    setShowForm(true);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const optimizedFile = await optimizeGalleryImage(file);
      const fileUrl = await uploadImage(optimizedFile, 'albums/covers');
      setFormData(prev => ({ ...prev, cover_url: fileUrl }));
      toast.success('Capa enviada em formato otimizado!');
    } catch (error) {
      toast.error('Erro ao enviar imagem.');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedPhotos = [];
      for (const file of files) {
        const optimizedFile = await optimizeGalleryImage(file);
        const fileUrl = await uploadImage(optimizedFile, 'albums/photos');
        uploadedPhotos.push({ url: fileUrl, caption: '' });
      }
      setFormData(prev => ({ 
        ...prev, 
        photos: [...prev.photos, ...uploadedPhotos],
        cover_url: prev.cover_url || uploadedPhotos[0]?.url
      }));
      toast.success(`${uploadedPhotos.length} foto(s) otimizada(s) e enviada(s)!`);
    } catch (error) {
      toast.error('Erro ao enviar fotos.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Nome do álbum é obrigatório.');
      return;
    }

    if (formData.download_url) {
      try {
        const parsedUrl = new URL(formData.download_url);
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
          throw new Error('invalid');
        }
      } catch {
        toast.error('Informe um link externo válido começando com http ou https.');
        return;
      }
    }

    const photos = formData.photos || [];
    const payload = {
      ...formData,
      cover_url: formData.cover_url || photos[0]?.url || '',
      photos,
      download_url: formData.download_url.trim()
    };

    if (editingAlbum) {
      updateMutation.mutate({ id: editingAlbum.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-pink-500 to-rose-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Galeria" 
            subtitle={`${albums.length} álbuns`}
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {albums.length === 0 ? (
          <EmptyState
            icon={Image}
            title="Nenhum álbum cadastrado"
            description="Crie álbuns para a galeria."
            actionLabel="Criar Álbum"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {albums.map((album, index) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-xl shadow-sm overflow-hidden ${!album.is_active ? 'opacity-60' : ''}`}
              >
                {album.cover_url && (
                  <div className="aspect-video relative bg-gray-100">
                    <img
                      src={album.cover_url}
                      alt={album.name}
                      className="w-full h-full object-contain"
                    />
                    {!album.is_active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="bg-white px-3 py-1 rounded-full text-sm">Inativo</span>
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {album.photos?.length || 0} fotos
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900">{album.name}</h3>
                  {album.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{album.description}</p>
                  )}
                  {album.download_url && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                      <Download className="w-3.5 h-3.5" />
                      Link externo configurado
                    </div>
                  )}

                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleEdit(album)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200"
                      onClick={() => {
                        if (confirm('Excluir este álbum?')) {
                          deleteMutation.mutate(album.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAlbum ? 'Editar Álbum' : 'Novo Álbum'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome do Álbum *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Culto de Domingo"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Data do Evento</Label>
              <Input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
              />
            </div>

            <div>
              <Label>Link externo para download</Label>
              <div className="relative mt-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="url"
                  value={formData.download_url}
                  onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                  placeholder="Cole aqui a pasta pública do Google Drive"
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Use uma pasta pública do Google Drive, OneDrive ou outro serviço para disponibilizar as fotos em alta qualidade fora do app.
              </p>
            </div>

            <div>
              <Label>Fotos</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={photo.url}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotosUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">Adicionar</span>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Álbum ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingAlbum ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
